import { useEffect, useState } from "react";
import { IconButton, TableCell } from "@mui/material";
import Search from "@mui/icons-material/Search";

import { templateEmailNotificationHistoricalApi } from "@/main/api";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";
import KOGSection from "@/main/components/shared/kogSection/kogSection";
import KOGPaginationGrid from "@/main/components/shared/paginationGrid/paginationGrid";
import RenderTooltipMessage from "@/main/components/shared/renderTooltipMessage/renderTooltipMessage";
import { TemplateEmailNotificationHistoricalGridResponse } from "@/main/models/response/templateEmailNotificationResponse";
import { TemplateEmailNotificationHistoricalSearchGridRequest } from "@/main/models/request/templateEmailNotificationRequest";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getTranslateByKey } from "@/main/services/utils";

import EmailTemplateHistoricalModal from "./emailTemplateHistoricalModal/emailTemplateHistoricalModal";
import * as S from "./styles";

interface ISectionEmailTemplateHistoricalProps {
	guidCompany: string;
	idTemplateEmailNotification: number;
	idSystemApplication: number;
	refreshKey?: number;
	defaultVariableNames?: string[];
}

function SectionEmailTemplateHistorical({
	guidCompany,
	idTemplateEmailNotification,
	idSystemApplication,
	refreshKey,
	defaultVariableNames = [],
}: ISectionEmailTemplateHistoricalProps) {
	const ID_SCROLLABLE_TARGET = "id-scrollable-target-email-template-historical";

	const [dataGrid, setDataGrid] = useState<TemplateEmailNotificationHistoricalGridResponse[]>([]);
	const [formFilters, setFormFilters] = useState<TemplateEmailNotificationHistoricalSearchGridRequest | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedGuid, setSelectedGuid] = useState<string | null>(null);

	const rowHeaders = [
		getTranslateByKey("information.language"),
		getTranslateByKey("information.dateTimeUser"),
		getTranslateByKey("information.actions"),
	];

	const sizeColumns = ["35%", "responsive", "80px"];

	function initFilter() {
		setDataGrid([]);
		setFormFilters(null);
		const request: TemplateEmailNotificationHistoricalSearchGridRequest = {
			companyGuid: guidCompany,
			idTemplateEmailNotification,
			idSystemApplication,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		};
		setFormFilters(request);
	}

	useEffect(() => {
		if (!guidCompany || !idTemplateEmailNotification || !idSystemApplication) return;
		initFilter();
	}, [guidCompany, idTemplateEmailNotification, idSystemApplication, refreshKey]);

	async function fetchData(request: any) {
		return templateEmailNotificationHistoricalApi.searchGridHistorical(request).then((response) => response.data);
	}

	function handleOpenModal(guid: string) {
		setSelectedGuid(guid);
		setIsModalOpen(true);
	}

	function handleCloseModal() {
		setIsModalOpen(false);
		setSelectedGuid(null);
	}

	return (
		<KOGSection title={getTranslateByKey("information.history")}>
			<S.Container id={ID_SCROLLABLE_TARGET}>
				<KOGPaginationGrid
					fetchEqualFilters
					showEndMessage={false}
					showTitleResults={false}
					dataGrid={dataGrid}
					formFilters={formFilters}
					idScrollableTarget={ID_SCROLLABLE_TARGET}
					setDataGrid={setDataGrid}
					fetchMore={fetchData}
				>
					<BoxGrid marginCustom="0" rowHeaders={rowHeaders} sizeColumns={sizeColumns}>
						{dataGrid.map((item, index) => (
							<KOGTableRow key={item.templateEmailNotificationCompanyHistoricalGuid} $notClickable>
								<TableCell>{item.cultureName}</TableCell>
								<TableCell>{item.dateTimeUser}</TableCell>
								<TableCell>
									<IconButton
										id={`id-button-detail-historical-${index}`}
										onClick={() => handleOpenModal(item.templateEmailNotificationCompanyHistoricalGuid)}
									>
										<RenderTooltipMessage tooltipMessage={getTranslateByKey("information.historyDetails")}>
											<Search fontSize="medium" />
										</RenderTooltipMessage>
									</IconButton>
								</TableCell>
							</KOGTableRow>
						))}
					</BoxGrid>
				</KOGPaginationGrid>
			</S.Container>
			<EmailTemplateHistoricalModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				guid={selectedGuid}
				defaultVariableNames={defaultVariableNames}
			/>
		</KOGSection>
	);
}

export default SectionEmailTemplateHistorical;
